import React, { useState, useEffect, memo, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Paper,
  Avatar,
  Tooltip,
  Slide,
  useMediaQuery,
  useTheme,
  Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAllAnnonces } from "../store/annonceSlice";
import useFavorites from "../hooks/useFavorites";
import useCompare from "../hooks/useCompare";
import { renderDoc } from "../requests/s3";

// Hauteurs des barres pour la coordination
export const COMPARE_BAR_HEIGHT_DESKTOP = 120;
export const COMPARE_BAR_HEIGHT_MOBILE = 70;

type AnnoncePreview = {
  id: string;
  titre: string;
  prix: number;
  image: string;
};

const FavoritesBar = memo(() => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { favorites, favoritesCount, toggleFavorite } = useFavorites();
  const { compareCount } = useCompare();
  const allAnnonces = useSelector(selectAllAnnonces);
  const [previews, setPreviews] = useState<AnnoncePreview[]>([]);

  // Cache pour les images déjà chargées
  const imageCache = useRef<Map<string, string>>(new Map());
  // Référence pour annuler les chargements en cours
  const loadingRef = useRef<number>(0);

  // Calcule le décalage pour éviter la superposition avec CompareBar
  const compareBarOffset = compareCount > 0
    ? (isMobile ? COMPARE_BAR_HEIGHT_MOBILE : COMPARE_BAR_HEIGHT_DESKTOP)
    : 0;

  // Charge les aperçus des annonces favorites avec gestion des conditions de course
  useEffect(() => {
    const currentLoad = ++loadingRef.current;

    const loadPreviews = async () => {
      const newPreviews: AnnoncePreview[] = [];

      for (const id of favorites) {
        // Vérifie si le chargement a été annulé
        if (currentLoad !== loadingRef.current) return;

        const annonce = allAnnonces.find((a) => a._id === id);
        if (annonce) {
          let imageUrl = "";
          const thumbnailKey = annonce.data.thumbnail?.imageUrl;

          if (thumbnailKey) {
            // Vérifie le cache d'abord
            if (imageCache.current.has(thumbnailKey)) {
              imageUrl = imageCache.current.get(thumbnailKey)!;
            } else {
              try {
                const res = await renderDoc(thumbnailKey);
                if (res !== "error" && currentLoad === loadingRef.current) {
                  imageUrl = res.url;
                  imageCache.current.set(thumbnailKey, imageUrl);
                }
              } catch {
                // Silently fail, will show without image
              }
            }
          }

          newPreviews.push({
            id: annonce._id,
            titre: annonce.data.titre || "Annonce",
            prix: annonce.data.data.informations.prix.data,
            image: imageUrl,
          });
        }
      }

      // Ne met à jour que si c'est toujours le chargement en cours
      if (currentLoad === loadingRef.current) {
        setPreviews(newPreviews);
      }
    };

    if (favorites.length > 0) {
      loadPreviews();
    } else {
      setPreviews([]);
    }
  }, [favorites, allAnnonces]);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(price);

  const handleRemove = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    toggleFavorite(id);
  };

  const handleViewFavorites = () => {
    navigate("/favoris");
  };

  // Ne rien afficher si pas de favoris
  if (favoritesCount === 0) {
    return null;
  }

  return (
    <Slide direction="up" in={favoritesCount > 0} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          bottom: compareBarOffset,
          left: 0,
          right: 0,
          zIndex: 1300,
          backgroundColor: "white",
          borderTop: "3px solid",
          borderTopColor: "#e91e63",
          maxHeight: isMobile ? "25vh" : "150px",
          transition: "bottom 0.3s ease-in-out",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: { xs: 1.5, sm: 2 },
            gap: { xs: 1, sm: 2 },
          }}
        >
          {/* Section gauche - Info */}
          <Box display="flex" alignItems="center" gap={1}>
            <Badge badgeContent={favoritesCount} color="error" max={99}>
              <FavoriteIcon sx={{ color: "#e91e63", fontSize: { xs: 24, sm: 28 } }} />
            </Badge>
            <Box>
              <Typography variant={isMobile ? "body2" : "subtitle1"} fontWeight={600}>
                Mes Favoris
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                {favoritesCount} bien{favoritesCount > 1 ? "s" : ""} sauvegardé
                {favoritesCount > 1 ? "s" : ""}
              </Typography>
            </Box>
          </Box>

          {/* Section milieu - Aperçus (desktop uniquement) */}
          {!isMobile && (
            <Box
              display="flex"
              gap={1}
              sx={{
                flex: 1,
                overflowX: "auto",
                overflowY: "hidden",
                maxWidth: "60%",
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "grey.300",
                  borderRadius: 3,
                },
              }}
            >
              {previews.slice(0, 5).map((preview) => (
                <Tooltip
                  key={preview.id}
                  title={
                    <Box>
                      <Typography variant="caption" display="block">
                        {preview.titre}
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {formatPrice(preview.prix)}
                      </Typography>
                    </Box>
                  }
                >
                  <Box
                    sx={{
                      position: "relative",
                      minWidth: 80,
                      cursor: "pointer",
                      "&:hover": {
                        opacity: 0.8,
                      },
                    }}
                    onClick={() => navigate(`/annonce/${preview.id}`)}
                  >
                    {preview.image ? (
                      <Avatar
                        src={preview.image}
                        variant="rounded"
                        sx={{ width: 80, height: 80 }}
                      />
                    ) : (
                      <Avatar variant="rounded" sx={{ width: 80, height: 80 }}>
                        <FavoriteIcon />
                      </Avatar>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleRemove(e, preview.id)}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        backgroundColor: "white",
                        boxShadow: 1,
                        "&:hover": {
                          backgroundColor: "error.light",
                          color: "white",
                        },
                        width: 24,
                        height: 24,
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Tooltip>
              ))}
              {favoritesCount > 5 && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    minWidth: 80,
                    height: 80,
                    backgroundColor: "grey.100",
                    borderRadius: 1,
                    cursor: "pointer",
                  }}
                  onClick={handleViewFavorites}
                >
                  <Typography variant="body2" fontWeight={600}>
                    +{favoritesCount - 5}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Section droite - Actions */}
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              onClick={handleViewFavorites}
              sx={{
                backgroundColor: "#e91e63",
                "&:hover": {
                  backgroundColor: "#c2185b",
                },
              }}
            >
              {isMobile ? "Voir" : "Voir tous mes favoris"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
});

FavoritesBar.displayName = "FavoritesBar";

export default FavoritesBar;
